import { PerformanceLog } from "../types/post";
import { unitToSeconds } from "./timeDurationUtils";

export interface PersonalBestEvaluation {
  isNewPersonalBest: boolean;
  previousBest: number | null;
  /** Light linear-trend projection of the next result, not a statistical guarantee. */
  predictedNextBest: number | null;
}

export interface EvaluatePersonalBestOptions {
  /** When editing, exclude this log so "previous best" is not the row on screen. */
  excludeLogId?: string;
}

// Variables where a lower measurement is the better result (race/split times).
const LOWER_IS_BETTER_VARIABLES = new Set(["time", "rest"]);

function isLowerIsBetter(variableName: string): boolean {
  return LOWER_IS_BETTER_VARIABLES.has(variableName.toLowerCase());
}

function normalizeUnit(unit?: string): string {
  return (unit || "").trim().toLowerCase();
}

/**
 * Convert a stored measurement into a single comparable base unit so history
 * logged in different units (kg vs lbs, km vs mi, pace vs speed) can be ranked.
 *
 * Bases: time/rest → seconds, weight → kg, distance → meters, speed → m/s.
 * Returns null for non-finite or non-positive time/rest (0 must not beat 60s).
 */
export function toComparableValue(
  variableName: string,
  value: number,
  unit?: string,
): number | null {
  if (!Number.isFinite(value)) return null;

  const name = variableName.toLowerCase();
  const u = normalizeUnit(unit);

  if (name === "time") {
    // Always stored as raw seconds regardless of the unit field.
    return value > 0 ? value : null;
  }

  if (name === "rest") {
    const seconds = unitToSeconds(value, unit);
    return seconds != null && seconds > 0 ? seconds : null;
  }

  if (name === "weight") {
    if (u === "lbs" || u === "lb") return value * 0.45359237;
    if (u === "g") return value / 1000;
    // Default / kg
    return value;
  }

  if (name === "distance") {
    if (u === "km") return value * 1000;
    if (u === "mi" || u === "mile" || u === "miles") return value * 1609.344;
    if (u === "yd" || u === "yard" || u === "yards") return value * 0.9144;
    if (u === "ft" || u === "foot" || u === "feet") return value * 0.3048;
    if (u === "m" || u === "meter" || u === "meters" || u === "metre" || u === "metres") {
      return value;
    }
    // Default unit for Distance is km when unspecified.
    if (!u) return value * 1000;
    return value;
  }

  if (name === "speed") {
    if (u === "km/h" || u === "kph") return value / 3.6;
    if (u === "mph") return value * 0.44704;
    if (u === "m/s") return value;
    // Pace units: minutes per distance → m/s
    if (u === "min/km") return value > 0 ? 1000 / (value * 60) : null;
    if (u === "min/mi") return value > 0 ? 1609.344 / (value * 60) : null;
    return value;
  }

  return value;
}

function isBetterResult(
  value: number,
  best: number,
  lowerIsBetter: boolean,
): boolean {
  return lowerIsBetter ? value < best : value > best;
}

/**
 * Compares a just-entered result for an activity/variable against the
 * athlete's own logged history, and projects the next likely result with a
 * simple linear-trend fit over recent attempts (last 8, including this one).
 */
export function evaluatePersonalBest(
  pastLogs: (PerformanceLog & { id: string })[],
  activityTypeID: string,
  variableName: string,
  currentValue: number,
  currentUnit?: string,
  options?: EvaluatePersonalBestOptions,
): PersonalBestEvaluation {
  const lowerIsBetter = isLowerIsBetter(variableName);
  const comparableCurrent = toComparableValue(
    variableName,
    currentValue,
    currentUnit,
  );

  // Invalid / zero time-like values must never stamp a PB.
  if (comparableCurrent == null) {
    return {
      isNewPersonalBest: false,
      previousBest: null,
      predictedNextBest: null,
    };
  }

  const excludeId = options?.excludeLogId;
  const history = pastLogs
    .map((log) => {
      if (excludeId && log.id === excludeId) return null;
      if (log.activity?.activityTypeID !== activityTypeID) return null;
      const variable = log.activity.variables.find(
        (v) => v.name.toLowerCase() === variableName.toLowerCase(),
      );
      if (variable?.measurement == null) return null;
      const value = toComparableValue(
        variableName,
        variable.measurement,
        variable.unit,
      );
      if (value == null) return null;
      return {
        date: log.performanceDate || "",
        value,
      };
    })
    .filter((entry): entry is { date: string; value: number } => entry !== null)
    .sort((a, b) => a.date.localeCompare(b.date));

  const previousBest = history.length
    ? history.reduce(
        (best, entry) =>
          isBetterResult(entry.value, best, lowerIsBetter) ? entry.value : best,
        history[0].value,
      )
    : null;

  const isNewPersonalBest =
    previousBest === null ||
    isBetterResult(comparableCurrent, previousBest, lowerIsBetter);

  const recentValues = [
    ...history.map((h) => h.value),
    comparableCurrent,
  ].slice(-8);
  let predictedNextBest: number | null = null;
  if (recentValues.length >= 3) {
    const n = recentValues.length;
    const xs = recentValues.map((_, i) => i);
    const xMean = xs.reduce((a, b) => a + b, 0) / n;
    const yMean = recentValues.reduce((a, b) => a + b, 0) / n;
    const denominator = xs.reduce((sum, x) => sum + (x - xMean) ** 2, 0);
    const slope =
      denominator === 0
        ? 0
        : xs.reduce(
            (sum, x, i) => sum + (x - xMean) * (recentValues[i] - yMean),
            0,
          ) / denominator;
    const intercept = yMean - slope * xMean;
    const projected = intercept + slope * n;
    // Drop nonsense projections (e.g. negative race times from a 0 entry).
    if (Number.isFinite(projected) && (!lowerIsBetter || projected > 0)) {
      predictedNextBest = projected;
    }
  }

  return { isNewPersonalBest, previousBest, predictedNextBest };
}
